"use client"

import { TransformedStoreItem } from "@/(admin)/modules/ui-builder/config/themes/tamda-foods/StoreLocator/types"
import { Images } from "@/constants/images"
import { getFixedT } from "@/languages/i18n"
import Image from "next/image"
import Link from "next/link"

interface ContactInfoProps {
    store: TransformedStoreItem | null
    locale?: string
}

/** googleMapCode có thể là full URL hoặc Plus Code (vd "MXQJ+JX Usti nad Labem") */
const buildGoogleMapsUrl = (code: string) => {
    if (code.startsWith("http://") || code.startsWith("https://")) return code
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(code)}`
}

const ContactInfo = ({ store, locale }: ContactInfoProps) => {
    const t = getFixedT(locale)
    if (!store) {
        return (
            <div className="w-full sm:w-[27.5%] bg-white p-6">
                <p className="text-[14px] leading-[21.7px] text-[#333]">{t("No store selected")}</p>
            </div>
        )
    }

    return (
        <div className="w-full p-2 bg-[#F7F8FA] flex flex-col">
            {/* Contact Section */}
            <div className="flex flex-col gap-6">

                <div className="flex flex-col gap-6 sm:pl-[55px] sm:py-9 sm:p-5 p-4 pl-0">
                    {/* Address */}
                    <h2 className="text-lg sm:text-xl sm:leading-[31px] font-bold leading-[27.9px] text-[#333] uppercase">{store.name}</h2>
                    <div className="flex flex-col sm:gap-4 gap-[6px]">
                        {store.address && (
                            <div className="flex items-start gap-2">
                                <div className="relative w-[14px] h-[14px] flex-shrink-0 mt-0.5">
                                    <Image
                                        src={Images.iconLocationStore}
                                        fill
                                        alt="icon-location"
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                                <span className="text-sm sm:text-base leading-[21.7px] text-[#333]">{store.address}</span>
                            </div>
                        )}

                        {/* Email */}
                        {store.email && (
                            <div className="flex items-center gap-2">
                                <div className="relative w-[14px] h-[14px] flex-shrink-0">
                                    <Image
                                        src={Images.iconEmail}
                                        fill
                                        alt="icon-mail"
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                                <Link
                                    href={`mailto:${store.email}`}
                                    className="text-sm sm:text-base leading-[21.7px] text-[#333] hover:text-[#00A651]"
                                >
                                    {store.email}
                                </Link>
                            </div>
                        )}

                        {/* Phone */}
                        {store.phone && (
                            <div className="flex items-center gap-2">
                                <div className="relative w-[14px] h-[14px] flex-shrink-0">
                                    <Image
                                        src={Images.iconPhone}
                                        fill
                                        alt="icon-phone"
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                                <Link
                                    href={`tel:${store.phone.replace(/\s+/g, "")}`}
                                    className="text-sm sm:text-base leading-[21.7px] text-[#333] hover:text-[#00A651]"
                                >
                                    {store.phone}
                                </Link>
                            </div>
                        )}


                    </div>
                    <div className="flex flex-col sm:gap-4 gap-[6px]">
                        {/* GPS and Google Maps Links */}
                        {store.latitude && store.longitude && (
                            <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm sm:text-base leading-[24.8px] text-[#20AFFF] font-normal">
                                        GPS:
                                    </span>
                                    <Link href={`https://www.google.com/maps?q=${store.latitude},${store.longitude}`} target="_blank" rel="noopener noreferrer" className="text-sm sm:text-base text-[#20AFFF] leading-[21.7px] text-[#333]">
                                        {store.latitude.toFixed(6)}, {store.longitude.toFixed(6)}
                                    </Link>
                                </div>
                                {store.googleMapCode && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm sm:text-base leading-[24.8px] text-[#20AFFF] font-normal">
                                            Google Maps:
                                        </span>
                                        <Link
                                            href={buildGoogleMapsUrl(store.googleMapCode)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-sm sm:text-base text-[#20AFFF] leading-[24.8px] text-[#333]"
                                        >
                                            {t("View on Google Maps")}
                                        </Link>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Separator */}
            <div className="w-full h-px bg-[#00000033]" />

            {/* Opening Hours Section */}
            <div className="flex flex-col sm:gap-[25px] gap-[17px] sm:pl-[55px] sm:py-9 p-5 !pr-0">
                <h2 className="text-sm sm:text-xl sm:leading-[31px] font-bold leading-[21.7px] text-[#333] uppercase">
                    {t("Opening hours")}
                </h2>

                {store.openingHours && store.openingHours.length > 0 ? (
                    <div className="grid sm:grid-cols-2 grid-cols-1 justify-space-between sm:w-[500px] gap-y-4">
                        {store.openingHours.map((item, idx) => {
                            const totalItems = store.openingHours!.length
                            const isLastItem = idx === totalItems - 1
                            const isOddTotal = totalItems % 2 !== 0
                            const shouldCenter = isLastItem && isOddTotal

                            return (
                                <div
                                    key={idx}
                                    className={`flex justify-between items-center border-b border-[#0000001a] sm:w-[200px] w-[300px] ${shouldCenter ? 'sm:col-span-2 sm:mx-auto sm:max-w-[300px]' : ''
                                        }`}
                                >
                                    <span className="text-sm leading-[21.7px] text-[#333] font-normal">{t(item.day)}</span>
                                    <span className="text-sm leading-[21.7px] text-[#333] font-bold">{item.hours}</span>
                                </div>
                            )
                        })}
                    </div>
                ) : (
                    <p className="text-sm sm:text-base leading-[21.7px] text-[#333]">{t("No opening hours available")}</p>
                )}
            </div>

            {/* Guidance (HTML từ CKEditor) */}
            {store.guidance && (
                <>
                    <div className="w-full h-px bg-[#00000033]" />
                    <div className="flex flex-col gap-3 sm:pl-[55px] sm:py-9 p-5 pl-0">
                        <div
                            className="max-w-none text-sm sm:text-base leading-[21.7px] text-[#333] [&_p]:mb-1 [&_p:last-child]:mb-0 [&_strong]:font-bold [&_a]:text-[#20AFFF] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_h2]:font-bold [&_h3]:font-bold [&_h4]:font-bold"
                            dangerouslySetInnerHTML={{ __html: store.guidance }}
                        />
                    </div>
                </>
            )}
        </div>
    )
}

export default ContactInfo
